import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const variantMediaId = event.detail.resource?.featured_media?.id ?? event.detail.resource?.featured_image?.id;
    const variantMediaSrc =
      event.detail.resource?.featured_media?.preview_image?.src ?? event.detail.resource?.featured_image?.src;
    const fallbackTerms = this.#getVariantFallbackTerms(event.detail.resource);

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    if (!variantMediaId && !variantMediaSrc && fallbackTerms.length === 0) return;

    this.#selectVariantMediaWhenReady(newMediaGallery, variantMediaId, variantMediaSrc, fallbackTerms);
  };

  /**
   * Switches media immediately when the customer selects a variant option.
   *
   * @param {import('./events').VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    const mediaId = event.detail.resource?.mediaId;
    const mediaSrc = event.detail.resource?.mediaSrc;

    if (!mediaId && !mediaSrc) return;

    this.selectMediaById(mediaId, mediaSrc);
  };

  /**
   * The replacement gallery and slideshow refs initialize asynchronously, so retry briefly.
   *
   * @param {Element} mediaGallery
   * @param {string | number | null | undefined} mediaId
   * @param {string | null | undefined} mediaSrc
   * @param {number} [attempt]
   */
  #selectVariantMediaWhenReady(mediaGallery, mediaId, mediaSrc, fallbackTerms = [], attempt = 0) {
    requestAnimationFrame(() => {
      mediaGallery.selectMediaById?.(mediaId, mediaSrc, fallbackTerms);

      if (attempt >= 5) return;

      setTimeout(() => {
        this.#selectVariantMediaWhenReady(mediaGallery, mediaId, mediaSrc, fallbackTerms, attempt + 1);
      }, 50);
    });
  }

  /**
   * Selects the product media that belongs to the currently selected variant.
   *
   * @param {string | number | null | undefined} mediaId - Shopify media ID from the variant payload.
   * @param {string | null | undefined} mediaSrc - Shopify image URL from the variant payload.
   */
  selectMediaById(mediaId, mediaSrc, fallbackTerms = []) {
    const index = this.#getMediaIndex(mediaId, mediaSrc, fallbackTerms);

    if (index < 0) return;

    this.slideshow?.select(index, undefined, { animate: false });
    this.#scrollGridMediaIntoView(index);
  }

  /**
   * Finds a media index in the rendered gallery by Shopify media ID.
   *
   * @param {string | number | null | undefined} mediaId
   * @param {string | null | undefined} mediaSrc
   * @returns {number}
   */
  #getMediaIndex(mediaId, mediaSrc, fallbackTerms = []) {
    const normalizedMediaId = this.#normalizeMediaId(mediaId);
    const normalizedMediaSrc = this.#normalizeMediaSrc(mediaSrc);
    const normalizedFallbackTerms = fallbackTerms.map((term) => this.#normalizeMediaMatchText(term)).filter(Boolean);

    if (!normalizedMediaId && !normalizedMediaSrc && normalizedFallbackTerms.length === 0) return -1;

    const mediaItems = [...this.querySelectorAll('.product-media[data-media-id]')];
    const uniqueMediaItems = mediaItems.filter(
      (item, index, items) =>
        items.findIndex((candidate) => candidate.getAttribute('data-media-id') === item.getAttribute('data-media-id')) ===
        index
    );

    if (normalizedMediaId) {
      const mediaIndex = uniqueMediaItems.findIndex(
        (item) => this.#normalizeMediaId(item.getAttribute('data-media-id')) === normalizedMediaId
      );

      if (mediaIndex >= 0) return mediaIndex;
    }

    if (!normalizedMediaSrc && normalizedFallbackTerms.length === 0) return -1;

    const mediaSrcIndex = normalizedMediaSrc
      ? uniqueMediaItems.findIndex((item) => {
          const image = item.querySelector('img');

          return this.#normalizeMediaSrc(image?.currentSrc || image?.src) === normalizedMediaSrc;
        })
      : -1;

    if (mediaSrcIndex >= 0 || normalizedFallbackTerms.length === 0) return mediaSrcIndex;

    return uniqueMediaItems.findIndex((item) => {
      const image = item.querySelector('img');
      const mediaText = this.#normalizeMediaMatchText(`${image?.alt ?? ''} ${image?.currentSrc || image?.src || ''}`);

      return normalizedFallbackTerms.some((term) => mediaText.includes(term));
    });
  }

  /**
   * Keeps desktop grid galleries aligned with the selected variant media.
   *
   * @param {number} index
   */
  #scrollGridMediaIntoView(index) {
    if (this.presentation !== 'grid') return;

    const media = this.media?.[index];

    if (!media) return;

    media.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  /**
   * Shopify can expose media IDs as numbers, strings, or GIDs. Compare on the numeric tail.
   *
   * @param {string | number | null | undefined} mediaId
   * @returns {string}
   */
  #normalizeMediaId(mediaId) {
    return String(mediaId ?? '')
      .split('/')
      .pop()
      ?.trim();
  }

  /**
   * Normalizes Shopify CDN image URLs so width parameters do not affect matching.
   *
   * @param {string | null | undefined} mediaSrc
   * @returns {string}
   */
  #normalizeMediaSrc(mediaSrc) {
    try {
      const url = new URL(String(mediaSrc ?? ''), window.location.origin);
      return url.pathname.replace(
        /_[0-9]+x(?=\.)|_x[0-9]+(?=\.)|_pico(?=\.)|_icon(?=\.)|_thumb(?=\.)|_small(?=\.)|_compact(?=\.)|_medium(?=\.)|_large(?=\.)|_grande(?=\.)|_master(?=\.)/,
        ''
      );
    } catch (_error) {
      return '';
    }
  }

  /**
   * Builds fallback match terms for products whose variant images have not been assigned in Shopify.
   *
   * @param {Record<string, any> | null | undefined} variant
   * @returns {string[]}
   */
  #getVariantFallbackTerms(variant) {
    if (!variant) return [];

    return [variant.sku, variant.public_title, variant.title, ...(Array.isArray(variant.options) ? variant.options : [])]
      .filter((term) => typeof term === 'string')
      .filter((term) => term.trim() && term.trim().toLowerCase() !== 'default title');
  }

  /**
   * Normalizes SKU, option values, image alt text, and CDN filenames for loose matching.
   *
   * @param {string} text
   * @returns {string}
   */
  #normalizeMediaMatchText(text) {
    try {
      text = decodeURIComponent(text);
    } catch (_error) {
      // Keep the original text if it is not a valid URI component.
    }

    return String(text ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
